export const PLAN_CATALOG = Object.freeze([
  Object.freeze({
    type: 'mileage',
    name: 'Mileage Plan',
    shortName: 'Mileage',
    eyebrow: 'For vehicle-based work',
    monthlyPrice: 6,
    yearlyPrice: 60,
    savings: 12,
    usdMonthlyPrice: 5,
    usdYearlyPrice: 50,
    usdSavings: 10,
    featured: false,
    businessLimit: 1,
    recurringExpenseLimit: 0,
    recurringInvoiceLimit: 0,
    accountantLimit: 0,
    highlights: Object.freeze([
      'One Business mileage workspace',
      'Vehicles, trips and route-assisted distance',
      'Saved places, templates and mileage exports'
    ])
  }),
  Object.freeze({
    type: 'solo',
    name: 'Solo Plan',
    shortName: 'Solo',
    eyebrow: 'For independent operators',
    monthlyPrice: 12,
    yearlyPrice: 120,
    savings: 24,
    usdMonthlyPrice: 9,
    usdYearlyPrice: 90,
    usdSavings: 18,
    featured: false,
    businessLimit: 1,
    recurringExpenseLimit: 10,
    recurringInvoiceLimit: 0,
    accountantLimit: 1,
    highlights: Object.freeze([
      'One Business workspace',
      'Complete expense, receipt and bank-review workflow',
      'Manual invoices, mileage and tax-readiness tools'
    ])
  }),
  Object.freeze({
    type: 'entrepreneur',
    name: 'Entrepreneur Plan',
    shortName: 'Entrepreneur',
    eyebrow: 'For growing operators',
    monthlyPrice: 25,
    yearlyPrice: 250,
    savings: 50,
    usdMonthlyPrice: 19,
    usdYearlyPrice: 190,
    usdSavings: 38,
    featured: true,
    businessLimit: 3,
    recurringExpenseLimit: 50,
    recurringInvoiceLimit: 25,
    accountantLimit: 2,
    highlights: Object.freeze([
      'Up to 3 Business workspaces',
      'Contractor bills, payments and proof',
      'Recurring customer invoices and reminders'
    ])
  }),
  Object.freeze({
    type: 'portfolio',
    name: 'Portfolio Plan',
    shortName: 'Portfolio',
    eyebrow: 'For multi-business owners',
    monthlyPrice: 45,
    yearlyPrice: 450,
    savings: 90,
    usdMonthlyPrice: 34,
    usdYearlyPrice: 340,
    usdSavings: 68,
    featured: false,
    businessLimit: 5,
    recurringExpenseLimit: 250,
    recurringInvoiceLimit: 100,
    accountantLimit: 5,
    highlights: Object.freeze([
      'Up to 5 Business workspaces',
      'Higher automation limits across every Business',
      'Up to 5 external accountant relationships'
    ])
  })
]);

export const PLAN_ORDER = Object.freeze(PLAN_CATALOG.map((plan) => plan.type));

export const PLAN_LIMITS = Object.freeze(
  Object.fromEntries(PLAN_CATALOG.map((plan) => [plan.type, plan.businessLimit]))
);

export const PLAN_NAMES = Object.freeze(
  Object.fromEntries(PLAN_CATALOG.map((plan) => [plan.type, plan.name]))
);

export const PLAN_FEATURES = Object.freeze({
  financialRecords: Object.freeze(['solo', 'entrepreneur', 'portfolio']),
  mileageTracking: Object.freeze(['mileage', 'solo', 'entrepreneur', 'portfolio']),
  freelancerPayments: Object.freeze(['entrepreneur', 'portfolio']),
  recurringInvoices: Object.freeze(['entrepreneur', 'portfolio'])
});

export const PLAN_COMPARISON_GROUPS = Object.freeze([
  Object.freeze({
    label: 'Everyday records',
    features: Object.freeze([
      Object.freeze({label: 'Business workspaces', values: Object.freeze(['1', '1', 'Up to 3', 'Up to 5'])}),
      Object.freeze({label: 'Expenses, receipt OCR and emailed receipts', values: Object.freeze([false, true, true, true])}),
      Object.freeze({label: 'Income records', values: Object.freeze([false, true, true, true])}),
      Object.freeze({label: 'Bank imports, classification and reconciliation', values: Object.freeze([false, true, true, true])}),
      Object.freeze({label: 'Payment methods and owner repayments', values: Object.freeze([false, true, true, true])})
    ])
  }),
  Object.freeze({
    label: 'Invoices and contractors',
    features: Object.freeze([
      Object.freeze({label: 'Manual customer invoices, public links and PDF', values: Object.freeze([false, true, true, true])}),
      Object.freeze({label: 'Recurring customer invoice schedules per Business', values: Object.freeze(['—', '—', '25', '100'])}),
      Object.freeze({label: 'Contractor bills, payments and proof', values: Object.freeze([false, false, true, true])})
    ])
  }),
  Object.freeze({
    label: 'Mileage and readiness',
    features: Object.freeze([
      Object.freeze({label: 'Trips, vehicles and route-assisted mileage', values: Object.freeze([true, true, true, true])}),
      Object.freeze({label: 'Saved places, route templates and commute clarity', values: Object.freeze([true, true, true, true])}),
      Object.freeze({label: 'Mileage reports and exports', values: Object.freeze([true, true, true, true])}),
      Object.freeze({label: 'Monthly financial review and accountant-ready exports', values: Object.freeze([false, true, true, true])}),
      Object.freeze({label: 'Tax Centre and guided year-end ZIP', values: Object.freeze([false, true, true, true])})
    ])
  }),
  Object.freeze({
    label: 'Automation and collaboration',
    features: Object.freeze([
      Object.freeze({label: 'Active recurring expense schedules per Business', values: Object.freeze(['—', '10', '50', '250'])}),
      Object.freeze({label: 'External accountant relationships', values: Object.freeze(['—', '1', '2', '5'])}),
      Object.freeze({label: 'Support tickets and community feature voting', values: Object.freeze([true, true, true, true])}),
      Object.freeze({label: 'Installable PWA and offline trip capture', values: Object.freeze([true, true, true, true])})
    ])
  })
]);

export function getPlanCatalogItem(planType) {
  return PLAN_CATALOG.find((plan) => plan.type === planType) || null;
}
